<template>
  <DataTable
    v-model:multi-sort-meta="multiSortMeta"
    data-cy="scenarios-data-table"
    sort-mode="multiple"
    :first="offset"
    :value="scenarios"
    :total-records="scenariosAggregateCount"
    :rows="limit"
    :selection-mode="checkedRows ? 'multiple' : undefined"
    :selection="checkedRows"
    auto-layout
    lazy
    paginator
    paginator-position="bottom"
    paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
    responsive-layout="stack"
    current-page-report-template="Showing {first} to {last} of {totalRecords}"
    @update:first="$emit('update:offset', $event)"
    @update:limit="$emit('update:limit', $event)"
    @update:selection="$emit('update:checked-rows', $event)"
  >
    <template #empty>
      <p class="text-center" data-cy="no-scenarios-found">
        No scenarios found.
      </p>
    </template>
    <Column
      v-if="!!checkedRows"
      selectionMode="multiple"
      headerStyle="width: 3rem"
    ></Column>
    <column field="title" header="Name" :sortable="true">
      <template #body="{ data }">
        <router-link
          v-if="renderLinks"
          :to="{
            name: 'WellInfo',
            params: { sizingId: data.sizing_id, scenarioId: data.id }
          }"
          data-cy="scenario-name-link"
        >
          {{ data.title || "Untitled" }}
        </router-link>
        <span v-else>
          {{ data.title || "Untitled" }}
        </span>
      </template>
    </column>
    <column
      v-if="!hideWell"
      field="sizing.well.well_name"
      header="Well"
      :sortable="true"
    >
      <template #body="{ data }">
        <router-link
          v-if="data.well?.id && renderLinks"
          :to="{
            name: 'Well',
            params: { well: data.sizing.well.id }
          }"
        >
          {{ data.sizing.well.well_name }}
        </router-link>
        <span v-else-if="data.well?.id">
          {{ data.well.well_name }}
        </span>
      </template>
    </column>
    <column
      field="sizing.customer.customer_name"
      header="Customer"
      :sortable="true"
    />
    <column
      v-if="!hideOwner"
      field="author.display_name"
      header="Owner"
      :sortable="false"
    />
    <column field="sizing" header="Sizing">
      <template #body="{ data }">
        <router-link
          :to="{
            name: 'Sizing',
            params: { sizingId: data.sizing?.id }
          }"
          >{{ data.sizing.title }}
        </router-link>
      </template>
    </column>
    <column field="journal_updated_at" header="Last Updated" :sortable="true">
      <template #body="{ data }">
        {{ new Date(data.journal_updated_at).toLocaleDateString() }}
      </template>
    </column>
  </DataTable>
</template>

<script setup lang="ts">
import type { ScenariosQuery, Scenarios_Order_By } from "gql/api";

import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {
  type SortMeta,
  usePrimeVueMultisort
} from "@/composables/primevue/tables";
import { watch } from "vue";

const props = defineProps<{
  scenarios: ScenariosQuery["scenarios"];
  scenariosAggregateCount: number;
  limit: number;
  offset: number;
  multiSortMeta?: SortMeta[] | null;
  orderBy: Scenarios_Order_By[];
  hideOwner?: boolean;
  hideWell?: boolean;
  checkedRows?: ScenariosQuery["scenarios"];
  renderLinks?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:multi-sort-meta", value: SortMeta[]): void;
  (e: "update:order-by", value: Scenarios_Order_By[]): void;
  (e: "update:offset", value: number): void;
  (e: "update:limit", value: number): void;
  (e: "update:checked-rows", value: ScenariosQuery["scenarios"]): void;
}>();

const { multiSortMeta, orderBy } = usePrimeVueMultisort(props.orderBy);
if (props.multiSortMeta) {
  multiSortMeta.value = props.multiSortMeta;
}

watch(multiSortMeta, () => {
  emit("update:offset", 0);
  emit("update:order-by", orderBy.value);
  emit("update:multi-sort-meta", multiSortMeta.value);
});
</script>

<style scoped>
.p-highlight a {
  color: white;
}
</style>
